import { isEmpty, flatten } from 'lodash';
import React from 'react';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { Icon } from 'Components/frameworks/icon/Icon';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Title } from 'Components/frameworks/title/Title';
import { ReservedServicesList } from 'Components/widgets/reserved-services-wrapper/ReservedServicesList';
import { TravelDetails } from 'Components/widgets/travel-details/TravelDetails';
import { updateBookingCart } from 'Components/widgets/cart/cart.actions';
import { TransfersService } from 'Components/widgets/cart/TransfersService';
import { CartServices } from 'Components/widgets/cart/CartServices';
import { InsuranceService } from 'Components/widgets/cart/InsuranceService';
import { CartPaymentButton } from 'Components/widgets/cart/CartPaymentButton';
import { WhiteLabelService } from 'Components/widgets/cart/WhiteLabelService';
type Props = {
  cart: Cart;
  reservedServicesByAttendee: Record<string, ReadonlyArray<ReservedServicesByAttendee>>;
  showPaymentButton: boolean;
  stay: StayEntity;
  updateCart: typeof updateBookingCart;
  isCartLocked?: boolean;
};
export const BookingCart = ({
  cart,
  reservedServicesByAttendee,
  showPaymentButton = false,
  stay,
  updateCart,
  isCartLocked = false,
}: Props) => {
  const { trackClickEvent } = useTrackEvent();

  if (isEmpty(stay) || isEmpty(cart) || cart.isEmpty) {
    return null;
  }

  return cart.stayServices
    .filter(({ stayIndex }) => stayIndex === stay.stayIndex)
    .map((service, index) => {
      const childcareCart = service?.childcares || [];
      const pickRoomCart = service?.pickRoom || [];
      return (
        <SiteContainer key={index} className="py-20" size="medium">
          <Title className="mb-20" theme="title-brand-n3">
            {t('cart.service.title')}
          </Title>
          <Capsule className="mb-40" contentClassName="p-20 sm:py-10">
            <TravelDetails
              bookingStatus={cart?.booking?.status as unknown as BookingStatus}
              stay={stay}
            />
          </Capsule>
          <Title className="mb-20" theme="title-brand-n3">
            {t('cart.itemsToBePaid')}
          </Title>
          <div>
            {!isEmpty(childcareCart) && (
              <CartServices
                bookingId={stay.booking.id}
                iconLabel={t('cart.service.childcareLabel')}
                iconName={ICONS.CHILDREN}
                serviceName="CHILDCARE"
                services={childcareCart}
                stayAvailableServices={stay.upsells.childcares}
                stayIndex={stay.stayIndex}
                updateCart={updateCart}
                isCartLocked={isCartLocked}
              />
            )}
            {!isEmpty(service.transfers) && (
              <TransfersService
                bookingId={stay.booking.id}
                iconLabel={t('cart.service.transferLabel')}
                price={
                  (
                    (
                      service.transfers[0] as unknown as {
                        schedules?: Array<{ attendees?: Array<{ price: number }> }>;
                      }
                    )?.schedules || []
                  ).reduce(
                    (sum, schedule) =>
                      sum + (schedule.attendees || []).reduce((acc, { price }) => acc + price, 0),
                    0,
                  ) ||
                  cart.price.detail.find(
                    ({ service: serviceDetail }) => serviceDetail === 'TRANSFER',
                  )?.amount ||
                  0
                }
                transfers={(service.transfers || []) as CartTransfer[]}
                stayIndex={stay.stayIndex}
                updateCart={updateCart}
                isCartLocked={isCartLocked}
              />
            )}
            {!isEmpty(
              (
                service as unknown as {
                  insurance?: Array<{
                    schedules?: Array<{ attendees?: Array<{ price: number }> }>;
                  }>;
                }
              ).insurance,
            ) && (
              <InsuranceService
                bookingId={stay.booking.id}
                iconLabel={t('cart.service.insuranceLabel')}
                insurance={
                  ((
                    service as unknown as {
                      insurance?: Array<{
                        schedules?: Array<{ attendees?: Array<{ price: number }> }>;
                      }>;
                    }
                  ).insurance?.[0] || {}) as CartTransfer
                }
                price={
                  (
                    (
                      service as unknown as {
                        insurance?: Array<{
                          schedules?: Array<{ attendees?: Array<{ price: number }> }>;
                        }>;
                      }
                    ).insurance?.[0]?.schedules || []
                  ).reduce(
                    (sum, schedule) =>
                      sum + (schedule.attendees || []).reduce((acc, { price }) => acc + price, 0),
                    0,
                  ) ||
                  cart.price.detail.find(
                    ({ service: serviceDetail }) => serviceDetail === 'INSURANCE',
                  )?.amount ||
                  0
                }
                updateCart={updateCart}
                stayIndex={stay.stayIndex}
                isCartLocked={isCartLocked}
              />
            )}
            {!isEmpty(service.whiteLabelServices) &&
              (service.whiteLabelServices || []).map((whiteLabelService) => (
                <WhiteLabelService
                  key={whiteLabelService.id}
                  bookingId={stay.booking.id}
                  iconLabel={t('cart.externalService.title')}
                  price={(
                    (
                      whiteLabelService as unknown as {
                        schedules?: Array<{ attendees?: Array<{ price: number }> }>;
                      }
                    ).schedules?.[0]?.attendees || []
                  ).reduce((acc, { price }) => acc + price, 0)}
                  service={
                    whiteLabelService as unknown as {
                      id: string;
                      type: ServiceType;
                      serviceInfo: { label: string };
                    }
                  }
                  stayIndex={stay.stayIndex}
                  updateCart={updateCart}
                  isCartLocked={isCartLocked}
                />
              ))}
            {pickRoomCart.length > 0 && (
              <Capsule>
                <div className="flex p-10">
                  <div className="flex flex-col items-center justify-center">
                    <Icon className="text-sienna" name={ICONS.ROOM} width="2rem" />
                    <span className="whitespace-nowrap text-center text-12 text-sienna">
                      {t('cart.service.pickYourRoomLabel')}
                    </span>
                  </div>
                  <div className="text-bold mx-10 flex w-full items-center">
                    {t('cart.service.pickYourRoomSelectedRoomText') +
                      ' ' +
                      flatten(
                        pickRoomCart.reduce(
                          (acc, s) =>
                            acc.concat(
                              (s.accommodations || []).map(({ rooms }) =>
                                rooms.map(({ id }) => id),
                              ),
                            ),
                          [],
                        ),
                      ).join(', ')}
                  </div>
                  <button
                    className="flex items-center justify-center px-10 leading-0"
                    type="button"
                  >
                    <Icon name={ICONS.CROSS} />
                  </button>
                </div>
              </Capsule>
            )}
          </div>
          {(!isEmpty(service.rentals) || !isEmpty(service.activities)) &&
            !isEmpty(reservedServicesByAttendee) && (
              <div className="mt-20">
                <ReservedServicesList
                  currency={cart.price.total.currency}
                  reservedServicesByAttendee={reservedServicesByAttendee[stay.stayIndex]}
                  scrollBackwardLabel={t('common.scrollBackward')}
                  scrollForwardLabel={t('common.scrollForward')}
                  noEditMode={isCartLocked}
                />
              </div>
            )}
          {showPaymentButton && (
            <div className="mt-20 flex justify-center">
              <CartPaymentButton
                onClick={() =>
                  trackClickEvent(TRACK_EVENT_NAMES.CLICK_CART, {
                    detail_click: 'validate_cart_opened',
                  })
                }
                stay={stay}
                hasOptionServices={
                  (cart as Cart & { hasServicesInOption?: boolean }).hasServicesInOption
                }
              />
            </div>
          )}
        </SiteContainer>
      );
    });
};
